import { FormBase } from "commonAssets/components/FormBase";
import { createElement } from "commonAssets/lib/createElement";
import { modal } from "commonAssets/lib/modal";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { loadMember } from "commonAssets/lib/loadMember";
import { ConfirmEmailAddress } from "commonAssets/lib/ConfirmEmailAddress";
import { localDatetime } from "commonAssets/lib/localDatetime";
import { generateMd5Hash } from "commonAssets/lib/generateMd5Hash";
import { toggleFieldWarning } from "commonAssets/lib/toggleFieldWarning";
import { removeClaimFile } from "commonAssets/lib/removeClaimFile";
import { uploadFile } from "commonAssets/lib/uploadFile";
import { validOTP } from "commonAssets/lib/validOTP";
import { createOTPForm } from "commonAssets/lib/createOTPForm";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import {
    i18n,
    loading,
    outputDiv,
    reCAPTCHAv3,
} from "commonAssets/js/constants";
import { appPayMethod } from "commonAssets/js/constants";
import {
    dstPaymentIframeTitle,
    dstMember,
    dstProcess,
} from "commonAssets/components/FormBase/DigitalDisbursements";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";

/**
 * Submit Claim
 *
 * @param {string} data - The claim form data.
 * @param {string} language - The language to use.
 * @param {function} customInitClaim - The custom init claim function.
 * @param {function} customProcessClaim - The custom process claim function.
 * @param {function} customLoadMember - The custom load member function.
 * @param {ErrorHandler} errorHandler - The error handler.
 * @returns {void}
 */

export class SubmitClaim extends FormBase {
    constructor(
        data,
        language,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler()
    ) {
        super(data, language);
        this.customInitClaim = customInitClaim;
        this.customProcessClaim = customProcessClaim;
        this.customLoadMember = customLoadMember;
        this.customDstProcess = customDstProcess;
        this.errorHandler = errorHandler;
    }

    async form() {
        try {
            const response = await fetch(this.data);
            const json = await FormBase.resolveHtmlFields(
                await response.json(),
                { baseUrl: response.url }
            );
            const form = json.claim_form[this.lang];
            this.init_claim(form);
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Error fetching claim form", { cause: error })
            );
            this.errorHandler.display(error);
        }
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            // document.getElementById('guard').classList.add("d-none");
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                            // TODO: add required text to labels
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    populate_claim_form(form, container, class_member, claim_deadline) {
        console.log("[populate_claim_form]");
        // set user for error tracking
        this.errorHandler.set_user({
            id: class_member.zero_zero_id,
            email: class_member.email_address,
        });
        // remove guard
        document.getElementById("guard").remove();
        // claim_deadline format December 31, 2024 at 04:00:00 P.M.
        const deadlineSpan = uniformClaimDeadline(claim_deadline);
        // New Claim Form
        container.appendChild(this.claim_form(form, deadlineSpan));
        container.scrollIntoView({ behavior: "smooth" });
        // output
        outputDiv.appendChild(container);
        // hide loading gif
        loadingGif(loading, "hide", 250);
        // Load member data into form
        loadMember(class_member);
        // custom login member code
        if (this.customLoadMember != null) {
            this.customLoadMember(this.claim);
        }
        // Payment widget
        if (appPayMethod && appPayMethod !== "") {
            dstMember();
        }
        // Init claim fields
        this.init_claim_fields(this.claim, form, claim_deadline);
    }

    init_claim_fields(claim, form, claim_deadline) {
        // Claim Inputs
        this.init_claim_inputs();

        // Loop thru available inputs
        this.init_claim_inputs_loop();

        // Validate phone number
        this.setup_phone_number_validation();

        // Country field toggle
        this.setup_country_field_toggle();

        // Confirm email address
        if (
            document.getElementById("email_address") &&
            document.getElementById("confirm_email_address")
        ) {
            ConfirmEmailAddress("init");
        }

        // Check for file documentation section
        if (document.getElementById("documentation")) {
            // Initialize file uploads
            this.init_claim_files(this.claim);
        }

        dstPaymentIframeTitle(form);

        if (Boolean(document.getElementById("form"))) {
            document.getElementById("form").setAttribute("novalidate", "true");

            // ada compliance updates - added
            // TODO: review if this is where this belongs
            // if id="submit-claim" is clicked, add was-validated to form class
            document
                .getElementById("submit-claim")
                .addEventListener("click", () => {
                    document
                        .getElementById("form")
                        .classList.add("was-validated");
                });

            document
                .getElementById("form")
                .addEventListener("submit", (process) => {
                    this.process_claim(process, this.claim, claim_deadline);
                });
        }

        if (this.customInitClaim != null) {
            this.claim = this.customInitClaim(this.claim) || this.claim;
        }

        loadingGif(loading, "hide", 1000);
    }

    init_claim_inputs() {
        const claimInputsContact =
            "#full_name, #first_name, #middle_initial, #last_name, #title, #business_name, #street_address_1, #street_address_2, #country, #city, #state, #zip_code, #province, #postal_code, #email_address, #confirm_email_address, #phone_number_1, #phone_number_2, #signature, #date, #ssn, #referer_url, #user_language, #ag_SGUID, #ag_TEST";
        const claimInputsFields =
            "#claim-fields input, #claim-fields textarea, #claim-fields select";
        const claimInputsFieldsPayment =
            "#payment_method, #payment_token, #payment_email, #payment_phone";
        const claimInputsTransactions =
            "#transactions input, #transactions textarea, #transactions select";

        this.claim.inputs.contact = Array.from(
            document.querySelectorAll(claimInputsContact)
        );
        this.claim.inputs.fields = Array.from(
            document.querySelectorAll(
                claimInputsFields + ", " + claimInputsFieldsPayment
            )
        );
        this.claim.inputs.transactions = Array.from(
            document.querySelectorAll(claimInputsTransactions)
        );
    }

    init_claim_inputs_loop() {
        for (const type in this.claim.inputs) {
            if (this.claim.inputs[type] && this.claim.inputs[type].length > 0) {
                this.claim.inputs[type].forEach(function (field) {
                    // Check for validation attribute
                    if (field.hasAttribute("data-validate")) {
                        // Attach change event handler for validation
                        field.addEventListener("change", function (e) {
                            validateField(field);
                        });
                    }
                });
            }
        }
    }

    /**
     * Initialize the claim files by attaching event listeners to relevant
     * elements.
     *
     * @param {Claim} claim - The claim object.
     * @returns {void}
     */
    init_claim_files(claim) {
        /**
         * Attach an event listener to the 'file-upload' element and perform
         * certain actions when the file selection changes.
         *
         * @param {Event} e - The change event object.
         * @returns {void}
         */
        document
            .getElementById("file-upload")
            .addEventListener("change", function (e) {
                if (validateField(this)) {
                    /* Generate File Hash */
                    generateMd5Hash(this.files[0], claim);
                    document.getElementById("file-description").focus();
                }
            });

        /**
         * Attach an event listener to the 'file-add' element and invoke the
         * 'add_claim_file' method when clicked.
         *
         * @param {Event} e - The click event object.
         * @returns {void}
         */
        document.getElementById("file-add").addEventListener("click", (e) => {
            this.add_claim_file();
        });

        /**
         * Attach an event listener to the 'file-description' element and
         * perform certain actions when a key is pressed.
         *
         * @param {KeyboardEvent} e - The keypress event object.
         * @returns {void}
         */
        document
            .getElementById("file-description")
            .addEventListener("keypress", (e) => {
                if (["Enter", "NumpadEnter"].includes(e.code)) {
                    console.log(e.code);
                    this.add_claim_file();
                    e.preventDefault();
                }
            });
    }

    process_claim(e, claim, deadline) {
        console.log("[process_claim]");

        /**
         * Prevent the default form submission.
         *
         * @var {Event} e - The submit event object.
         * @returns {void}
         */
        e.preventDefault();

        /**
         * Disable the submit button to prevent multiple submissions.
         *
         * Since: 2023.09.25
         *
         * @returns {void}
         */
        document.getElementById("submit-claim").disabled = true;

        // var claim = this.claim;
        if (claim.flags.sent) {
            return;
        }

        clearMessage();

        claim.flags.valid = true;
        claim.data.fields = {};

        this.init_claim_inputs();

        this.format_claim_data(claim);

        claim.data.contact.date = localDatetime();

        if (document.getElementById("documentation")) {
            this.process_claim_files(this.claim);
        }

        if (
            document.getElementById("email_address") &&
            document.getElementById("confirm_email_address")
        ) {
            ConfirmEmailAddress("process", this.claim);
        }

        // if appPayMethod is defined and not empty, we run dstProcess from FormBase/DigitalDisbursements.js
        if (
            appPayMethod &&
            appPayMethod !== "" &&
            this.customDstProcess === null
        ) {
            claim = dstProcess(this.claim);
        } else if (
            appPayMethod &&
            appPayMethod !== "" &&
            this.customDstProcess !== null
        ) {
            claim = this.customDstProcess(this.claim);
        }

        /**
         * Define the environment for the s3 uploader.
         */
        const s3env = this.config.url;

        // Custom process claim
        if (this.customProcessClaim != null) {
            claim = this.customProcessClaim(this.claim);
        }

        if (claim.flags.valid) {
            grecaptcha.enterprise
                .execute(reCAPTCHAv3, { action: "submit" })
                .then((token) => {
                    claim.data.token = token;
                    this.post_claim(claim, s3env);
                });
        } else {
            document.getElementById("submit-claim").disabled = false;
            modal(
                i18n.getTranslation("error"),
                i18n.getTranslation("claim_form_errors")
            );
        }
    }

    process_claim_files(claim) {
        console.log("[process_claim_files]");

        const error = document.querySelector(
            'label[for="file-upload"] .error'
        ).innerText;
        const file = document.getElementById("file-upload");

        toggleFieldWarning(file, false);

        if (file.classList.contains("error") && error != "No File Selected") {
            claim.flags.valid = false;
        } else if (file.value != "") {
            toggleFieldWarning(file, true, "Did you forget to add this file?");
            claim.flags.valid = false;
        } else {
            toggleFieldError(file, false);
        }
    }

    add_claim_file(e) {
        console.log(`[Add Claim File]`);
        const claim = this.claim;
        const file = document.getElementById("file-upload");
        const description = document.getElementById("file-description");
        const list = document.getElementById("file-list");
        const fileHash = document.getElementById("file-hash");
        let li;

        if (file.classList.contains("error")) {
            return;
        }

        toggleFieldWarning(file, false);

        toggleFieldError(file, false);
        if (!file.files[0]) {
            toggleFieldError(file, true, "No File Selected");
            return;
        }

        description.value = description.value.trim();

        toggleFieldError(description, false);
        if (!description.value) {
            description.focus();
            toggleFieldError(description, true, "Required");
            return;
        }

        li = document.createElement("li");
        li.innerHTML =
            '<span class="remove" style="cursor: pointer;"><i class="bi bi-x-circle text-danger d-inline-block float-end"></i><span></span></span><span class="filename"><strong>' +
            file.files[0].name +
            '</strong></span><span class="description">(' +
            description.value +
            ")</span>";
        list.appendChild(li);

        list.classList.remove("d-none");
        document.getElementById("no-files").classList.add("d-none");

        claim.uploads.push({
            file: file.files[0],
            description: description.value,
            hash: fileHash.value,
        });

        file.value = "";
        description.value = "";
        fileHash.value = "";

        li.querySelector(".remove").addEventListener("click", function (e) {
            removeClaimFile.call(this, e, claim);
        });

        /**
         * Handles the removal of a claim file from the file list and updates
         * the UI accordingly.
         *
         * @function removeClaimFile
         * @param {Event} e - The event object triggered by the click action.
         * @description
         * This function performs the following actions:
         * 1. Identifies the clicked row and its parent container.
         * 2. Confirms with the user if they want to remove the file,
         *      displaying the file name and description.
         * 3. Removes the file from the DOM and the `claim.uploads` array if
         *      confirmed.
         * 4. Updates the visibility of the file list and the "no files"
         *      message based on the remaining files.
         *
         * @deprecated 2025.06.19 - This function is deprecated and will be removed in future versions.
         * @since 2025.03.26
         * @version 2025.03.26
         * @returns {void}
         */
        // function removeClaimFile(e) {
        //     let row = this.parentElement;
        //     let parent = row.parentElement;
        //     let added = parent.querySelectorAll('li:not([disabled])');
        //     let index = [].indexOf.call(added, row);
        //     let confirmText = `Are you sure you want to remove this file? \nFile Name: ${claim.uploads[index].file.name}\nDescription: ${claim.uploads[index].description}`;

        //     if (window.confirm(confirmText)) {
        //         parent.removeChild(row);
        //         claim.uploads.splice(index, 1);
        //     }

        //     added = parent.querySelectorAll('li:not([disabled])'); // Recalculate after removal
        //     document.getElementById('file-list').classList[(added.length == 1) ? 'add' : 'remove']('d-none');
        //     document.getElementById('no-files').classList[(added.length == 1) ? 'remove' : 'add']('d-none');
        // }
    }

    format_claim_data(claim) {
        for (var type in claim.inputs) {
            if (claim.inputs[type] && claim.inputs[type].length > 0) {
                claim.inputs[type].forEach(function (field) {
                    const label =
                        field.getAttribute("data-label") || field.name;
                    const item_id = field.getAttribute("item-id") || null;
                    let data_field;

                    if (
                        field.hasAttribute("data-validate") &&
                        !validateField(field)
                    ) {
                        claim.flags.valid = false;
                    } else {
                        if (item_id) {
                            if (!claim.data[type][item_id])
                                claim.data[type][item_id] = {};
                            data_field = claim.data[type][item_id];
                        } else {
                            data_field = claim.data[type];
                        }
                        switch (field.type) {
                            case "radio":
                                if (field.checked)
                                    data_field[label] = field.value;
                                break;
                            case "checkbox":
                                field.checked
                                    ? (data_field[label] = field.value)
                                    : (data_field = "No");
                                break;
                            default:
                                data_field[label] = field.value;
                        }
                    }
                });
            }
        }
    }

    async post_claim(claim, s3env) {
        console.log("[post_claim]");

        /**
         * Set the claim as sent.
         *
         * @returns {void}
         */
        claim.flags.sent = true;

        /**
         * Disable the submit button to prevent multiple submissions.
         *
         * Removed: 2023.09.25 (moved to process_claim) due to multiple submissions.
         *
         * @returns {void}
         */
        // document.getElementById('submit-claim').disabled = true;

        /**
         * Wait 0.5 seconds before display loading gif
         * (Lambda in sleep mode)
         *
         * @returns {void}
         */
        const t = window.setTimeout(() => {
            loadingGif(loading, "show");
        }, 250);

        // Update claim if an existing claim id
        if (claim.data.claim_id) {
            claim.flags.method = "claimsPut";
        }

        /**
         * Process the claim uploads and upload the files.
         */
        try {
            if (claim.uploads.length && claim.uploads.length > 0) {
                for (let f = 0; f < claim.uploads.length; f++) {
                    await uploadFile(claim, s3env, claim.uploads[f], f, 0);
                }
            }
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Error uploading claim files", { cause: error })
            );
            this.errorHandler.display_modal(error);
        }

        /**
         * Paul Pomerleau - October 18th, 2023
         * If the device has a local device token it's added to the claim fields before posting
         */
        const deviceToken = localStorage.getItem("X-Device-Token");
        if (deviceToken) claim.data.fields.device_token = deviceToken;

        // Post/Put claims data
        claim.api[claim.flags.method]({}, claim.data)
            .then(async function (result) {
                // Lambda error message
                if (result.data.error) {
                    clearTimeout(t);
                    claim.flags.success = false;
                    modal(i18n.getTranslation("error"), result.data.error);
                    return;
                }

                /**
                 * Save correlation id
                 */
                claim.data.correlation_id = result.data.correlation_id;
                claim.flags.success = true;
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error posting claim", { cause: error })
                );
                this.errorHandler.display_modal(error);
                claim.flags.success = false;
            })
            .finally(() => {
                // debugger;
                clearTimeout(t);
                loadingGif(loading, "hide", 250);
                if (claim.flags.success) {
                    this.destroy_claim(claim);
                } else {
                    document.getElementById("submit-claim").disabled = false;
                    claim.flags.sent = false;
                }

                // Remove editClaim link on Claim Confirmation
                const editClaim = document.getElementById("editClaim");
                if (editClaim !== null) {
                    editClaim.remove();
                }
            });
    }

    edit_claim(params) {
        console.log("[Edit Claim]");
        const notice = createElement(
            "p",
            [],
            [],
            i18n.getTranslation("claim_edit_notice")
        );
        const div = createElement(
            "div",
            {
                class: "my-3 text-center fw-bold text-info-emphasis text-uppercase",
                id: "editClaim",
            },
            [notice]
        );

        const guardRow = createElement(
            "div",
            { class: "row fs-5", id: "guardRow" },
            [this.notice_confirmation(params, "col")]
        );
        const form = createElement(
            "form",
            { class: "needs-validation", id: "guard", method: "post" },
            [guardRow]
        );
        const formContainer = createElement("div", [], [form]);

        outputDiv.appendChild(div);

        document
            .getElementById("editClaimBtn")
            .addEventListener("click", () => {
                modal(
                    i18n.getTranslation("claim_edit_title"),
                    formContainer.innerHTML
                );

                // Allows for DOM to load
                setTimeout(() => {
                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            div.remove(); // remove the editClaim div upon click

                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    console.log("Class member data init...");

                                    let claim_deadline;
                                    if (Boolean(result.data.claim_deadline)) {
                                        claim_deadline =
                                            result.data.claim_deadline;
                                    }

                                    const container = createElement("div", {
                                        class: "display-form",
                                    });

                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error fetching class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.correlation_id =
                                        result.data.correlation_id;
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added
                                    const form = params;

                                    // Check for existing claim number and add to claim data
                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;

                                        loadingGif(loading, "show");
                                        outputDiv.innerHTML = ``;
                                        loadingGif(loading, "hide", 250);

                                        this.send_otp(
                                            form,
                                            container,
                                            result.data,
                                            claim_deadline
                                        );
                                        return;
                                    }

                                    // Hide guard
                                    document
                                        .querySelector(
                                            '[data-bs-dismiss="modal"]'
                                        )
                                        .click();

                                    // Load member data into form
                                    loadMember(result.data);

                                    // Custom load claim
                                    if (this.customLoadMember != null) {
                                        // this.customLoadMember(this.claim); ada compliance updates - commented out
                                        this.customLoadMember(result.data); // ada compliance updates - changed
                                    }

                                    // Payment widget
                                    // console.log("this is being used???")
                                    this.payment_widget_populate(result);

                                    loadingGif(loading, "hide", 250);
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error fetching class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                }, 1000);
            });
    }

    /**
     * Populates the payment widget with the provided result.
     *
     * DEPRECIATED: 2023.12.27 - This is no longer used.
     *
     * @param {Object} result - The result object containing the payment information.
     */
    payment_widget_populate(result) {
        if (!result.class_member.claim_id) return;
        if (!document.getElementById("payment_method").value) return;
        document.getElementById("dst-payment").classList.add("d-none");
        document.getElementById("previous_payment_method").innerText =
            document.getElementById("payment_method").value;
        document
            .getElementById("update_payment_toggle")
            .addEventListener("click", function () {
                document
                    .getElementById("update_payment_message")
                    .classList.add("d-none");
                document
                    .getElementById("dst-payment")
                    .classList.remove("d-none");
            });
        document
            .getElementById("update_payment_message")
            .classList.remove("d-none");
    }

    destroy_claim(claim) {
        loadingGif(loading, "hide", 500); // left here

        if (claim.flags.success) {
            claim.data.confirmation = this.claim_data_confirmation(claim.data);
            document.querySelector("div.display-form").classList.add("d-none");

            outputDiv.appendChild(this.claim_confirmation(claim.data));
            outputDiv.scrollIntoView({ behavior: "smooth" });

            setTimeout(() => {
                modal(
                    i18n.getTranslation("confirmation_screen_additional")[
                        "title"
                    ],
                    i18n.getTranslation("confirmation_screen_additional")[
                        "body"
                    ]
                );
            }, 250);
        } else {
            modal(
                i18n.getTranslation("error"),
                i18n.getTranslation("claim_submit_error")
            );
        }

        // Loop thru each input and disable to prevent changes and resubmissions
        for (const type in claim.inputs) {
            if (claim.inputs[type] && claim.inputs[type].length > 0) {
                claim.inputs[type].forEach(function (field) {
                    field.disabled = true;
                });
            }
        }

        [].forEach.call(
            document.querySelectorAll('input[type="radio"]'),
            function (radio) {
                radio.setAttribute("disabled", "disabled");
            }
        );

        document
            .getElementById("submit-claim")
            .setAttribute("disabled", "disabled");
    }

    claim_data_confirmation(claim) {
        const confirmation = {
            title: i18n.getTranslation("confirmation_page")["title"],
            upload_fail:
                i18n.getTranslation("confirmation_page")["upload_fail"],
            print_note: i18n.getTranslation("confirmation_page")["print_note"],
            claim_details: {
                title: i18n.getTranslation("confirmation_page")[
                    "claim_details"
                ]["title"],
                correlation_id: claim.correlation_id,
                body: i18n.getTranslation("confirmation_page")["claim_details"][
                    "body"
                ],
                footer: i18n.getTranslation("confirmation_page")[
                    "claim_details"
                ]["footer"],
                edit: i18n.getTranslation("confirmation_page")["claim_details"][
                    "edit"
                ],
                excluded_keys: [
                    "Payment Token",
                    "Payment Email",
                    "Payment Phone",
                    "device_token",
                ],
            },
        };
        return confirmation;
    }

    send_otp(form, container, class_member, claim_deadline) {
        const otp_request = {
            authorizationToken: "",
            domain: class_member.domain,
            email_address: class_member.email_address,
        };
        this.claim.api
            .otpGet(otp_request)
            .then((result) => {
                if (result.status != 200) {
                    loadingGif(loading, "hide", 250);
                    modal(
                        i18n.getTranslation("error"),
                        i18n.getTranslation("claim_processing_error")
                    );
                    return;
                }
                loadingGif(loading, "hide", 250);
                modal(
                    i18n.getTranslation("otp_title"),
                    createOTPForm(otp_request.email_address).outerHTML
                );
                setTimeout(() => {
                    const otpFormElement = document.getElementById("otp-form");
                    if (!otpFormElement) {
                        return;
                    }
                    const otp = new VanillaOTP(".otp-inputs");
                    otpFormElement.addEventListener("submit", (e) => {
                        e.preventDefault();
                        if (validOTP(otp.getValue())) {
                            loading.classList.add("show");
                            document.getElementById("submit-otp").disabled =
                                true;
                            this.validate_otp(
                                otp.getValue(),
                                class_member,
                                form,
                                container,
                                claim_deadline
                            );
                        } else {
                            modal(
                                i18n.getTranslation("error"),
                                i18n.getTranslation("otp_validation_error")
                            );
                        }
                    });
                }, 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching OTP", { cause: error })
                );
                this.errorHandler.display_modal(error);
            });
    }

    validate_otp(otp, class_member, form, container, claim_deadline) {
        const otp_request = {
            domain: class_member.domain,
            email_address: class_member.email_address,
            code: otp,
        };
        this.claim.api
            .otpPost({}, otp_request)
            .then((result) => {
                if (result.status == 200) {
                    this.populate_claim_form(
                        form,
                        container,
                        class_member,
                        claim_deadline
                    );
                    // document.querySelector('[data-bs-dismiss="modal"]').click();
                    console.log("OTP Validated");
                    const MAX_RETRIES = 3;
                    const RETRY_INTERVAL = 250;
                    let count = 0;

                    const closeOtp = () => {
                        const modalCloseButton = document.querySelector(
                            '[data-bs-dismiss="modal"]'
                        );

                        if (modalCloseButton) {
                            console.log("Closing OTP Modal...");
                            modalCloseButton.click();
                        } else {
                            console.log("Waiting for OTP Modal to Close...");
                        }

                        count++;

                        if (count <= MAX_RETRIES) {
                            setTimeout(closeOtp, RETRY_INTERVAL);
                        }
                    };

                    closeOtp();
                }
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error validating OTP", { cause: error })
                );
                this.errorHandler.display_otp_modal(error);
            });
    }

    /**
     * Creates an instance of SubmitClaim.
     *
     * @param {Object} params - The parameters for creating the instance.
     * @param {string} params.appClaimForm - The path to the app claim form.
     * @param {string} params.userLanguage - The user's language.
     * @param {Function?} params.customInitClaim - Optional custom init claim function.
     * @param {Function?} params.customProcessClaim - Optional custom process claim function.
     * @param {Function?} params.customLoadMember - Optional custom load member function.
     * @param {Function?} params.customDstProcess - Optional custom dst process function.
     * @param {ErrorHandler?} params.errorHandler - Optional error handler.
     * @returns {SubmitClaim} - The created instance.
     */
    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new SubmitClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
